import { useCallback, useEffect, useRef } from 'react'

type Options = {
  /** Сколько курсор должен простоять, прежде чем считать это намерением. */
  delay?: number
  enabled?: boolean
}

/**
 * Префетч по наведению — но только если курсор действительно задержался.
 * Проводка через полсотни плиток шахматки не должна порождать полсотни запросов.
 */
export function useHoverIntent(onIntent: () => void, options: Options = {}) {
  const { delay = 450, enabled = true } = options
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clear = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  const onPointerEnter = useCallback(() => {
    if (!enabled) return
    clear()
    timer.current = setTimeout(() => {
      timer.current = null
      onIntent()
    }, delay)
  }, [clear, delay, enabled, onIntent])

  // Гасим таймер, который УЖЕ летит в момент выключения. Проверки в onPointerEnter мало:
  // она отсекает только старт нового.
  //
  // Иначе ломается ровно тот сценарий, ради которого enabled и существует: навёл курсор
  // на плитку брони (таймер пошёл) → через 200 мс зажал ЛКМ и потащил полотно шахматки
  // (enabled стал false) → курсор с плитки не уходил, значит onPointerLeave не звали,
  // значит на 450-й мс префетч всё равно выстрелит — посреди перетаскивания, которое мы
  // и хотели от него защитить.
  useEffect(() => {
    if (!enabled) clear()
  }, [enabled, clear])

  // Размонтирование: висящий таймер не должен пережить компонент.
  useEffect(() => clear, [clear])

  return { onPointerEnter, onPointerLeave: clear }
}
